/**
 * ActorDetailPage — Full detail view for a single Actor primitive.
 *
 * Route: /ferp-core/workforce/actors/:actorId
 *
 * Shows identity, type, roles, resolved scopes, and system config.
 * Deep-linkable from task assignments, audit records, or anywhere
 * an actor ID appears in the UI.
 */

import { Link, useParams } from 'react-router-dom';
import {
  Anchor,
  Badge,
  Breadcrumbs,
  Container,
  Divider,
  Group,
  Loader,
  Paper,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  Title,
  Alert,
} from '@mantine/core';
import {
  IconAlertCircle,
  IconCircleCheck,
  IconCircleX,
  IconShieldCheck,
  IconUser,
} from '@tabler/icons-react';

import {
  GqlActorType,
  useGetActorQuery,
  useListRolesQuery,
  useGetActorSecurityQuery,
} from '@/generated/graphql';
import { useTenantId } from '@/auth/AuthContext';
import { useMemo } from 'react';

// ============================================================================
// Helpers
// ============================================================================

const ACTOR_TYPE_COLOR: Record<string, string> = {
  [GqlActorType.HUMAN]: 'blue',
  [GqlActorType.AI]: 'grape',
  [GqlActorType.SYSTEM]: 'orange',
  [GqlActorType.EXTERNAL]: 'teal',
};

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

// ============================================================================
// Component
// ============================================================================

export function ActorDetailPage() {
  const { actorId } = useParams<{ actorId: string }>();
  const tenantId = useTenantId();

  const { data, loading, error } = useGetActorQuery({
    variables: { actorId: actorId! },
    skip: !actorId,
  });

  const { data: rolesData } = useListRolesQuery({ variables: { limit: 200 } });

  // DRR-197/§46: Feature surfacing — Actor security details (MFA, biometric, emergency access)
  const { data: securityData } = useGetActorSecurityQuery({
    variables: { id: actorId!, tenantId: tenantId ?? '' },
    skip: !actorId || !tenantId,
  });

  const roleNameMap = useMemo(() => {
    const map = new Map<string, string>();
    for (const r of rolesData?.listRoles?.items ?? []) {
      map.set(r.id, r.roleName);
    }
    return map;
  }, [rolesData]);

  // -------------------------------------------------------------------------
  // Loading / error states
  // -------------------------------------------------------------------------

  if (loading) {
    return (
      <Container size="xl" py="xl">
        <Stack gap="md">
          <Skeleton height={28} width={300} />
          <Skeleton height={20} width={200} />
          <SimpleGrid cols={{ base: 1, sm: 3 }} mt="md">
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} height={80} radius="md" />
            ))}
          </SimpleGrid>
        </Stack>
      </Container>
    );
  }

  if (error) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="red" title="Failed to load actor">
          {error.message}
        </Alert>
      </Container>
    );
  }

  const actor = data?.getActor;

  if (!actor) {
    return (
      <Container size="xl" py="xl">
        <Alert icon={<IconAlertCircle size={16} />} color="orange" title="Not found">
          Actor <code>{actorId}</code> does not exist or you lack access.
        </Alert>
      </Container>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Container size="xl" py="lg">
      <Stack gap="lg">
        {/* Breadcrumb */}
        <Breadcrumbs>
          <Anchor component={Link} to="/ferp-core/workforce" size="sm">
            Workforce
          </Anchor>
          <Anchor component={Link} to="/ferp-core/workforce" size="sm">
            Actors
          </Anchor>
          <Text size="sm">{actor.name}</Text>
        </Breadcrumbs>

        {/* Header */}
        <Group justify="space-between" align="flex-start">
          <Group gap="sm">
            <IconUser size={28} />
            <div>
              <Title order={2}>{actor.name}</Title>
              {actor.description && (
                <Text c="dimmed" size="sm">
                  {actor.description}
                </Text>
              )}
            </div>
          </Group>
          <Group gap="xs">
            <Badge
              color={ACTOR_TYPE_COLOR[actor.actorType] ?? 'gray'}
              variant="light"
              size="lg"
            >
              {actor.actorType}
            </Badge>
            <Badge
              color={actor.active ? 'green' : 'red'}
              variant="light"
              size="lg"
            >
              {actor.active ? 'Active' : 'Inactive'}
            </Badge>
          </Group>
        </Group>

        {/* Stat cards */}
        <SimpleGrid cols={{ base: 2, sm: 4 }}>
          <Paper p="sm" withBorder radius="md">
            <Text size="xs" c="dimmed">Category</Text>
            <Text size="sm" fw={600} tt="capitalize">
              {actor.category ?? '—'}
            </Text>
          </Paper>
          <Paper p="sm" withBorder radius="md">
            <Text size="xs" c="dimmed">Domain</Text>
            <Text size="sm" fw={600}>
              {actor.domain ?? '—'}
            </Text>
          </Paper>
          <Paper p="sm" withBorder radius="md">
            <Text size="xs" c="dimmed">Max Capacity</Text>
            <Text size="sm" fw={600}>
              {actor.maxCapacity}
            </Text>
          </Paper>
          <Paper p="sm" withBorder radius="md">
            <Text size="xs" c="dimmed">Available</Text>
            <Group gap={4}>
              {actor.available ? (
                <IconCircleCheck size={16} color="green" />
              ) : (
                <IconCircleX size={16} color="red" />
              )}
              <Text size="sm" fw={600}>
                {actor.available ? 'Yes' : 'No'}
              </Text>
            </Group>
          </Paper>
        </SimpleGrid>

        {/* Roles */}
        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Text fw={600} size="sm">
              Assigned Roles ({actor.roleIds.length})
            </Text>
            {actor.roleIds.length === 0 ? (
              <Text size="sm" c="dimmed">No roles assigned.</Text>
            ) : (
              <Group gap="xs" wrap="wrap">
                {actor.roleIds.map((rid) => (
                  <Badge key={rid} variant="outline" size="sm">
                    {roleNameMap.get(rid) ?? rid}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        </Paper>

        {/* Resolved Scopes */}
        <Paper p="md" withBorder radius="md">
          <Stack gap="sm">
            <Group gap="xs">
              <IconShieldCheck size={16} />
              <Text fw={600} size="sm">
                Resolved Scopes ({actor.resolvedScopes.length})
              </Text>
            </Group>
            {actor.resolvedScopes.length === 0 ? (
              <Text size="sm" c="dimmed">No scopes — assign roles to grant access.</Text>
            ) : (
              <Group gap={4} wrap="wrap">
                {actor.resolvedScopes.map((scope) => (
                  <Badge key={scope} variant="dot" size="sm" color="blue">
                    {scope}
                  </Badge>
                ))}
              </Group>
            )}
          </Stack>
        </Paper>

        {/* Circuit breaker */}
        {actor.circuitBreakerOpen && (
          <Alert
            icon={<IconAlertCircle size={16} />}
            color="red"
            title="Circuit Breaker Open"
          >
            This actor is currently paused from receiving new work. Threshold:{' '}
            {actor.circuitBreakerThreshold}.
          </Alert>
        )}

        {/* DRR-197/§46: Security Details (feature surfacing) */}
        {securityData?.actor && (
          <Paper p="md" withBorder radius="md">
            <Stack gap="sm">
              <Group gap="xs">
                <IconShieldCheck size={16} />
                <Text fw={600} size="sm">Security Status</Text>
              </Group>
              <SimpleGrid cols={{ base: 2, sm: 4 }}>
                <div>
                  <Text size="xs" c="dimmed">Security Level</Text>
                  <Badge
                    color={securityData.actor.securityLevel === 'life-critical' ? 'red' : 'blue'}
                    variant="light"
                    size="sm"
                  >
                    {securityData.actor.securityLevel}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">MFA</Text>
                  <Badge
                    color={securityData.actor.mfaEnabled ? 'green' : 'gray'}
                    variant="light"
                    size="sm"
                  >
                    {securityData.actor.mfaEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Biometric</Text>
                  <Badge
                    color={securityData.actor.biometricEnabled ? 'green' : 'gray'}
                    variant="light"
                    size="sm"
                  >
                    {securityData.actor.biometricEnabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div>
                  <Text size="xs" c="dimmed">Emergency Access</Text>
                  <Badge
                    color={securityData.actor.emergencyAccess ? 'orange' : 'gray'}
                    variant="light"
                    size="sm"
                  >
                    {securityData.actor.emergencyAccess ? 'Active' : 'None'}
                  </Badge>
                </div>
              </SimpleGrid>
              {securityData.actor.emergencyAccessLog.length > 0 && (
                <>
                  <Divider />
                  <Text size="xs" fw={500}>Recent Emergency Access ({securityData.actor.emergencyAccessLog.length})</Text>
                  {securityData.actor.emergencyAccessLog.slice(0, 3).map((event) => (
                    <Text key={event.eventId} size="xs" c="dimmed">
                      {event.accessedAt}: {event.reason} ({event.duration}min)
                    </Text>
                  ))}
                </>
              )}
            </Stack>
          </Paper>
        )}

        {/* Metadata */}
        <Divider />
        <SimpleGrid cols={{ base: 1, sm: 2 }}>
          <div>
            <Text size="xs" c="dimmed">Actor ID</Text>
            <Text size="sm" ff="monospace">{actor.id}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Primary Role</Text>
            <Text size="sm">
              {actor.primaryRoleId
                ? (roleNameMap.get(actor.primaryRoleId) ?? actor.primaryRoleId)
                : '—'}
            </Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Created</Text>
            <Text size="sm">{formatDate(actor.createdAt)}</Text>
          </div>
          <div>
            <Text size="xs" c="dimmed">Last Updated</Text>
            <Text size="sm">{formatDate(actor.updatedAt)}</Text>
          </div>
        </SimpleGrid>
      </Stack>
    </Container>
  );
}

export default ActorDetailPage;
